import React, { useEffect, useState } from 'react';
import { documentNumberLabel, invoiceStatusLabel } from './documentLabels';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Box, Button, Chip, CircularProgress, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import { CustomerService } from '../../services/customer.service';
import { Customer } from '../../interfaces/sales/customer.interface';
import { SalesInvoiceService } from '../../services/salesInvoice.service';
import { SalesInvoice, SALES_INVOICE_STATUS_COLORS } from '../../interfaces/sales/salesInvoice.interface';
import { useToast } from '../../context/ToastContext';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import ReassignDialog from './scope/ReassignDialog';
import { ActiveStatusChip, DeactivateDialog } from '../common/MasterDataStatus';
import { formatMoney, humanizeEnum } from '../../utils/format';
import { formatDate } from '../../utils/dateUtils';
import { ReceiptService } from '../../services/receipt.service';
import { Receipt } from '../../interfaces/sales/receipt.interface';
import RecordOnAccountReceiptDialog from './RecordOnAccountReceiptDialog';
import AttachmentsPanel from '../common/AttachmentsPanel';
import { CRM_ATTACHMENT_KINDS } from '../../services/attachment.service';
import CustomerPortalCard from './portal/CustomerPortalCard'; // P1-B

const CustomerDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const canManage = usePermission(PERMISSIONS.CUSTOMER_MANAGE);
  const canManagePortal = usePermission(PERMISSIONS.PORTAL_MANAGE); // P1-B
  const canReassign = usePermission(PERMISSIONS.SALES_REASSIGN);
  const [reassignOpen, setReassignOpen] = useState(false);
  // Invoices and the balance owed come from the invoice list, which needs the invoice-view permission.
  const canSeeInvoices = usePermission(PERMISSIONS.SALES_INVOICE_VIEW);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [deactivateOpen, setDeactivateOpen] = useState(false);
  const [invoices, setInvoices] = useState<SalesInvoice[]>([]);
  const canManageReceipts = usePermission(PERMISSIONS.RECEIPT_MANAGE);
  const canViewReceipts = usePermission(PERMISSIONS.RECEIPT_VIEW);
  const [receiveOpen, setReceiveOpen] = useState(false);
  const [unapplied, setUnapplied] = useState<Receipt[]>([]);

  const loadUnapplied = () => {
    if (!id || !canViewReceipts) return;
    ReceiptService.listUnappliedByCustomer(id)
      .then(list => setUnapplied(list))
      .catch(e => toast.showApiError(e, 'Failed to load on-account receipts'));
  };

  useEffect(() => {
    loadUnapplied();
  }, [id, canViewReceipts]); // eslint-disable-line react-hooks/exhaustive-deps

  const reloadInvoices = () => {
    if (!id || !canSeeInvoices) return;
    SalesInvoiceService.listByCustomer(id).then(setInvoices).catch(() => undefined);
  };

  useEffect(() => {
    if (!id) return;
    CustomerService.getById(id)
      .then(c => setCustomer(c))
      .catch(e => toast.showApiError(e, 'Failed to load customer'))
      .finally(() => setLoading(false));
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!id || !canSeeInvoices) return;
    SalesInvoiceService.listByCustomer(id)
      .then(list => setInvoices(list))
      .catch(e => toast.showApiError(e, 'Failed to load invoices'));
  }, [id, canSeeInvoices]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDelete = async () => {
    if (!customer) return;
    if (!window.confirm(`Delete customer "${customer.name}"? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await CustomerService.remove(customer.id);
      toast.showSuccess('Customer deleted');
      navigate('/sales/customers');
    } catch (e) {
      // Surface backend 400 (open documents) and other errors via toast — do NOT swallow.
      toast.showApiError(e, 'Failed to delete customer');
    } finally {
      setDeleting(false);
    }
  };

  const activate = async () => {
    if (!customer) return;
    try {
      await CustomerService.activate(customer.id);
      toast.showSuccess('Customer activated');
      setCustomer(await CustomerService.getById(customer.id));
    } catch (e) {
      toast.showApiError(e, 'Failed to activate customer');
    }
  };

  if (loading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;
  if (!customer) return <Box sx={{ p: 3 }}><Typography>Customer not found.</Typography></Box>;

  const outstanding = invoices
    .filter(inv => inv.status === 'ISSUED' || inv.status === 'PARTIALLY_PAID')
    .reduce((sum, inv) => sum + (inv.totalAmount - inv.paidAmount - (inv.creditedAmount ?? 0)), 0);

  return (
    <Box sx={{ p: 3 }} data-help-page="sales.customer.page">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h5">{customer.name}</Typography>
          <ActiveStatusChip active={customer.active} reason={customer.deactivationReason} />
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>Salesperson: {customer.salespersonName ?? 'Unassigned'}</Typography>
          {canReassign && <Button data-help="sales.customer.reassign" onClick={() => setReassignOpen(true)}>Reassign</Button>}
          {reassignOpen && (
            <ReassignDialog open type="customers" id={customer.id} title={customer.name} currentName={customer.salespersonName}
              onClose={() => setReassignOpen(false)} onDone={() => { if (id) CustomerService.getById(id).then(setCustomer).catch(e => toast.showApiError(e, 'Failed to reload customer')); }} />
          )}
          {canManageReceipts && (
            <Button data-help="sales.customer.receive" variant="contained" color="success" onClick={() => setReceiveOpen(true)}>
              Receive payment
            </Button>
          )}
          {canManage && (
            <Button
              data-help="sales.customer.edit"
              variant="outlined"
              onClick={() => navigate(`/sales/customers/${customer.id}/edit`)}
            >
              Edit
            </Button>
          )}
          {canManage && (customer.active === false ? (
            <Button data-help="sales.customer.activate" variant="outlined" onClick={activate}>Activate</Button>
          ) : (
            <Button data-help="sales.customer.deactivate" variant="outlined" color="warning" onClick={() => setDeactivateOpen(true)}>Deactivate</Button>
          ))}
          {canManage && (
            <Button
              data-help="sales.customer.delete"
              variant="outlined"
              color="error"
              onClick={handleDelete}
              disabled={deleting}
            >
              {deleting ? <CircularProgress size={20} /> : 'Delete'}
            </Button>
          )}
          <Button variant="outlined" data-help="sales.customer.back" onClick={() => navigate(-1)}>Back</Button>
        </Box>
      </Box>

      <Paper data-help-section="sales.customer.profile" sx={{ p: 3 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 1, alignItems: 'center' }}>
          <Typography color="text.secondary">Name</Typography>
          <Typography>{customer.name}</Typography>
          <Typography color="text.secondary">Contact Person</Typography>
          <Typography>{customer.contactPerson ?? '—'}</Typography>
          <Typography color="text.secondary">Email</Typography>
          <Typography>{customer.email ?? '—'}</Typography>
          <Typography color="text.secondary">Phone</Typography>
          <Typography>{customer.phone ?? '—'}</Typography>
          <Typography color="text.secondary">Address</Typography>
          <Typography>{customer.address ?? '—'}</Typography>
          <Typography color="text.secondary">Tax Reg No</Typography>
          <Typography>{customer.taxRegistrationNo ?? '—'}</Typography>
        </Box>
      </Paper>


      {canViewReceipts && (
        <Paper data-help-section="sales.customer.onAccount" sx={{ p: 3, mt: 3 }}>
          <Typography variant="h6" gutterBottom>On-account receipts (unapplied)</Typography>
          {unapplied.length === 0 ? (
            <Typography color="text.secondary">No unapplied receipts.</Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell data-help="sales.customer.onAccount.col.receipt">Receipt #</TableCell>
                    <TableCell data-help="sales.customer.onAccount.col.date">Date</TableCell>
                    <TableCell data-help="sales.customer.onAccount.col.method">Method</TableCell>
                    <TableCell align="right" data-help="sales.customer.onAccount.col.amount">Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {unapplied.map(r => (
                    <TableRow key={r.id} hover>
                      <TableCell>
                        <Link to={`/sales/receipts/${r.id}`}>{r.receiptNumber ?? 'View receipt'}</Link>
                      </TableCell>
                      <TableCell>{formatDate(r.receiptDate)}</TableCell>
                      <TableCell>{humanizeEnum(r.method)}</TableCell>
                      <TableCell align="right">{formatMoney(r.amount)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      <RecordOnAccountReceiptDialog
        open={receiveOpen}
        customerId={customer.id}
        customerName={customer.name}
        onClose={() => setReceiveOpen(false)}
        onPosted={() => { loadUnapplied(); reloadInvoices(); }}
      />

      {canSeeInvoices && (<>
      {/* Outstanding balance */}
      <Paper data-help-section="sales.customer.balance" sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" gutterBottom>Outstanding Balance</Typography>
        <Typography variant="h4" color={outstanding > 0 ? 'error' : 'text.primary'}>
          {formatMoney(outstanding)}
        </Typography>
      </Paper>

      {/* Recent invoices */}
      <Paper data-help-section="sales.customer.invoices" sx={{ p: 3, mt: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="h6">Recent Invoices</Typography>
          <Button
            variant="outlined"
            size="small"
            data-help="sales.customer.viewStatement"
            onClick={() => navigate(`/sales/reports/statement?customerId=${customer.id}`)}
          >
            View Statement
          </Button>
        </Box>
        {invoices.length === 0 ? (
          <Typography color="text.secondary">No invoices found.</Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell data-help="sales.customer.invoices.col.number">Invoice #</TableCell>
                  <TableCell data-help="sales.customer.invoices.col.issueDate">Issue Date</TableCell>
                  <TableCell align="right" data-help="sales.customer.invoices.col.total">Total</TableCell>
                  <TableCell data-help="sales.customer.invoices.col.status">Status</TableCell>
                  <TableCell align="right" data-help="sales.customer.invoices.col.balance">Balance</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invoices
                  .slice()
                  .sort((a, b) => (b.issueDate ?? '').localeCompare(a.issueDate ?? ''))
                  .slice(0, 5)
                  .map(inv => (
                    <TableRow
                      key={inv.id}
                      hover
                      component={Link}
                      to={`/sales/invoices/${inv.id}`}
                      sx={{ textDecoration: 'none', cursor: 'pointer' }}
                    >
                      <TableCell>{documentNumberLabel(inv.invoiceNumber)}</TableCell>
                      <TableCell>{formatDate(inv.issueDate)}</TableCell>
                      <TableCell align="right">
                        {formatMoney(inv.totalAmount)}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={invoiceStatusLabel(inv, humanizeEnum)}
                          color={SALES_INVOICE_STATUS_COLORS[inv.status]}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        {formatMoney(inv.totalAmount - inv.paidAmount - (inv.creditedAmount ?? 0))}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
      </>)}
      <Box data-help-section="sales.customer.attachments">
        <AttachmentsPanel entityType="CUSTOMER" entityId={customer.id!} canManage={canManage} kinds={CRM_ATTACHMENT_KINDS} />
      </Box>
      {/* ---- P1-B customer portal ---- */}
      {canManagePortal && customer.id && <CustomerPortalCard customerId={customer.id} />}
      {/* ---- end P1-B ---- */}
      <DeactivateDialog
        open={deactivateOpen}
        noun="customer"
        name={customer.name}
        onDeactivate={async (reason) => {
          try {
            return await CustomerService.deactivate(customer.id, reason);
          } catch (e) {
            toast.showApiError(e, 'Failed to deactivate customer');
            throw e;
          }
        }}
        onDeactivated={async () => {
          try { setCustomer(await CustomerService.getById(customer.id)); } catch { /* the header refreshes on the next load */ }
        }}
        onClose={() => setDeactivateOpen(false)}
      />
    </Box>
  );
};

export default CustomerDetail;
